import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Users, Zap, Flame, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AddPlayerModal } from '@/components/AddPlayerModal';
import { usePlayerProfiles, type PlayerWithProgress } from '@/hooks/usePlayerProfiles';
import { toast } from 'sonner';

const SPORT_EMOJI: Record<string, string> = {
  football: '🏈',
  soccer: '⚽',
  basketball: '🏀',
  baseball: '⚾',
};

const AVATAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
const MAX_CHILDREN = 3;

function MiniPlayerRow({ player, onDelete }: { player: PlayerWithProgress; onDelete: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const color = player.avatar_color || AVATAR_COLORS[0];

  return (
    <div className="flex items-center gap-3 p-2 rounded-xl bg-muted/40">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-display font-bold shrink-0"
        style={{ backgroundColor: `${color}20`, color }}
      >
        {(player.display_name || player.username).charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="font-display font-bold text-sm truncate">
            {player.display_name || player.username}
          </p>
          <span className="text-xs">{SPORT_EMOJI[player.sport] || '🏈'}</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground font-body">
          <span>@{player.username}</span>
          <span className="flex items-center gap-0.5"><Zap className="w-3 h-3" />{player.xp}</span>
          <span className="flex items-center gap-0.5"><Flame className="w-3 h-3" />{player.streak}</span>
          <span className="flex items-center gap-0.5"><BookOpen className="w-3 h-3" />{player.completed_lessons.length}</span>
        </div>
      </div>
      {confirm ? (
        <div className="flex flex-col gap-1">
          <Button variant="destructive" size="sm" onClick={onDelete} className="h-7 px-2 text-xs">
            Remove
          </Button>
          <button onClick={() => setConfirm(false)} className="text-[10px] text-muted-foreground">
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirm(true)}
          className="text-muted-foreground hover:text-destructive p-1.5 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export function ChildAccountsCard() {
  const { players, createPlayer, deletePlayer } = usePlayerProfiles();
  const [showAdd, setShowAdd] = useState(false);

  const handleDelete = async (player: PlayerWithProgress) => {
    try {
      await deletePlayer(player.id);
      toast.success(`${player.display_name || player.username} removed`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove child');
    }
  };

  const canAdd = players.length < MAX_CHILDREN;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14 }}
        className="bg-card rounded-2xl border-2 border-border p-5 shadow-card space-y-3"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" /> Child Accounts
            </h3>
            <p className="text-xs text-muted-foreground font-body mt-0.5">
              Add a child player you can supervise ({players.length}/{MAX_CHILDREN})
            </p>
          </div>
          {canAdd && (
            <Button
              onClick={() => setShowAdd(true)}
              size="sm"
              className="h-9 font-display font-bold rounded-xl gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Add Child
            </Button>
          )}
        </div>

        {players.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-4 text-center">
            <p className="text-sm font-body text-muted-foreground">
              No child accounts yet. Add one to give them their own username + PIN login,
              separate lessons, and progress you can monitor.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {players.map((p) => (
              <MiniPlayerRow key={p.id} player={p} onDelete={() => handleDelete(p)} />
            ))}
          </div>
        )}

        {players.length > 0 && (
          <p className="text-[10px] text-muted-foreground font-body">
            Manage each player in the <span className="font-bold text-foreground">Players</span> tab.
          </p>
        )}
      </motion.div>

      <AddPlayerModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreate={createPlayer}
        currentCount={players.length}
      />
    </>
  );
}
